import type { AnnotationsIntegrationSelectionState } from '../../core/components/annotations/components/indicators/types.js';
import type {
  ExploreState,
  InteractionState,
} from '../../core/components/interactions-state-helpers/types.js';
import type { SeriesQueryResult } from '../context/XYChartOverlayPerformance.context.js';
import type {
  AREA_VARIANT,
  AreaSeriesWithDatapoints,
  AxisIdToCurrentScaleDomain,
  BAR_VARIANT,
  BarSeriesWithDatapoints,
  DOT_VARIANT,
  DotSeriesWithDatapoints,
  LINE_VARIANT,
  LineSeriesWithDatapoints,
  RECT_VARIANT,
  RectDatapointInternal,
  RectSeriesWithDatapoints,
} from './xy-chart-internals.js';
import type {
  AreaDatapoint,
  BarDatapoint,
  DotDatapoint,
  LineDatapoint,
} from './xy-chart.js';

export type RectSeriesContent = {
  datapoint: RectDatapointInternal;
  series: RectSeriesWithDatapoints;
  type: typeof RECT_VARIANT;
};

export type BarSeriesContent = {
  datapoint: BarDatapoint;
  series: BarSeriesWithDatapoints;
  type: typeof BAR_VARIANT;
};

export type DotSeriesContent = {
  datapoint: DotDatapoint;
  series: DotSeriesWithDatapoints;
  type: typeof DOT_VARIANT;
};

export type LineSeriesContent = {
  datapoint: LineDatapoint;
  series: LineSeriesWithDatapoints;
  type: typeof LINE_VARIANT;
};

export type AreaSeriesContent = {
  datapoint: AreaDatapoint;
  series: AreaSeriesWithDatapoints;
  type: typeof AREA_VARIANT;
};

export const UndefinedContent = {
  datapoint: undefined,
  series: undefined,
  type: 'undefined',
} as const;

export type TooltipContent =
  | BarSeriesContent
  | RectSeriesContent
  | DotSeriesContent
  | LineSeriesContent
  | AreaSeriesContent;

export type XYChartTooltipMetadata = {
  preferred: TooltipContent;
  datapoints: SeriesQueryResult[];
};

export type XYChartExploreState = {
  content: TooltipContent | typeof UndefinedContent;
} & ExploreState;

export interface XYChartThresholdsState {
  hoveredThresholdId: string | undefined;
}

export interface XYChartState extends Omit<
  InteractionState<AxisIdToCurrentScaleDomain>,
  'explore'
> {
  filteredSeriesIds?: Set<string>;
  thresholds: XYChartThresholdsState;
  annotations: AnnotationsIntegrationSelectionState;
}
